import { useEffect } from "react";
import { DatePicker, Form, Input, InputNumber, Modal, Select, Typography } from "antd";
import dayjs, { type Dayjs } from "dayjs";

import {
  COLLECTIONS_CASE_STATUS_LABELS,
  COLLECTIONS_CASE_WORKFLOW_STATUSES,
  COLLECTIONS_SITUATION_LABELS,
  CollectionsCaseStatus,
  CollectionsSituation,
  type CollectionsCase,
} from "@fitness-app/data-models/entities/CollectionsCase";

import { type CollectionsCaseAction } from "../hooks/useCollections";

export type CaseActionKind = CollectionsCaseAction["type"];

interface CaseActionModalProps {
  kind: CaseActionKind | null;
  current: CollectionsCase | null;
  loading?: boolean;
  onCancel: () => void;
  onSubmit: (action: CollectionsCaseAction) => void;
}

const TITLES: Record<CaseActionKind, string> = {
  status: "Zmień status sprawy",
  situation: "Sytuacja klientki",
  owner: "Opiekun sprawy",
  declaration: "Deklaracja płatności",
  settlement: "Ugoda: nowy harmonogram",
  agency: "Przekaż do windykacji zewnętrznej",
  note: "Notatka",
};

const HINTS: Partial<Record<CaseActionKind, string>> = {
  declaration:
    "Sprawa przechodzi w „Oczekiwanie na płatność”; do terminu nie wychodzą żadne automatyczne wiadomości. Dzień po terminie pojawi się zadanie sprawdzenia wpłaty.",
  settlement:
    "Ugoda dla rat kartą to realna zmiana harmonogramu w Stripe: najpierw zmień plan rat w ustawieniach klientki, potem zapisz ugodę tutaj. Dopóki jest realizowana, nie windykujemy.",
  agency: "Telefony administracji się zatrzymują, saldo liczy się dalej. Za 60 dni pojawi się zadanie przeglądu.",
  status: "Zamknięcie sprawy anuluje otwarte zadania.",
};

type FormModel = {
  status?: CollectionsCaseStatus;
  situation?: CollectionsSituation | null;
  amountPln?: number;
  dueAt?: Dayjs | null;
  installments?: number;
  changeId?: string;
  agency?: string;
  reference?: string;
  note?: string;
};

const toAction = (kind: CaseActionKind, values: FormModel): CollectionsCaseAction | null => {
  const note = values.note?.trim() || undefined;
  switch (kind) {
    case "status":
      return values.status ? { type: "status", status: values.status, note } : null;
    case "situation":
      return { type: "situation", situation: values.situation ?? null, note };
    case "declaration":
      return values.amountPln && values.dueAt
        ? {
            type: "declaration",
            amount: Math.round(values.amountPln * 100),
            dueAt: values.dueAt.format("YYYY-MM-DD"),
            note,
          }
        : null;
    case "settlement":
      return values.installments && values.amountPln
        ? {
            type: "settlement",
            installments: values.installments,
            amount: Math.round(values.amountPln * 100),
            changeId: values.changeId?.trim() || null,
            note,
          }
        : null;
    case "agency":
      return {
        type: "agency",
        agency: values.agency?.trim() || "vindicat",
        reference: values.reference?.trim() || undefined,
        note,
      };
    case "note":
      return note ? { type: "note", note } : null;
    default:
      return null;
  }
};

const CaseActionModal = ({ kind, current, loading, onCancel, onSubmit }: CaseActionModalProps) => {
  const [form] = Form.useForm<FormModel>();

  useEffect(() => {
    if (kind) {
      form.resetFields();
      form.setFieldsValue({
        status:
          current?.status && !COLLECTIONS_CASE_WORKFLOW_STATUSES.includes(current.status) ? current.status : undefined,
        situation: current?.situation ?? null,
        agency: current?.externalCollection?.agency ?? "vindicat",
        installments: current?.settlement?.installments,
        amountPln: current?.settlement ? current.settlement.amount / 100 : undefined,
      });
    }
  }, [kind, current, form]);

  const handleOk = async () => {
    if (!kind) {
      return;
    }
    const values = await form.validateFields().catch(() => null);
    if (!values) {
      return;
    }
    const action = toAction(kind, values);
    if (action) {
      onSubmit(action);
    }
  };

  return (
    <Modal
      open={kind !== null}
      title={kind ? TITLES[kind] : ""}
      okText="Zapisz"
      cancelText="Anuluj"
      confirmLoading={loading}
      onCancel={onCancel}
      onOk={() => void handleOk()}
      destroyOnHidden
    >
      {kind && HINTS[kind] ? <Typography.Paragraph type="secondary">{HINTS[kind]}</Typography.Paragraph> : null}
      <Form form={form} layout="vertical">
        {kind === "status" ? (
          <Form.Item name="status" label="Status" rules={[{ required: true }]}>
            <Select
              options={Object.values(CollectionsCaseStatus)
                .filter((status) => !COLLECTIONS_CASE_WORKFLOW_STATUSES.includes(status))
                .map((status) => ({
                  value: status,
                  label: COLLECTIONS_CASE_STATUS_LABELS[status],
                }))}
            />
          </Form.Item>
        ) : null}
        {kind === "situation" ? (
          <Form.Item name="situation" label="Sytuacja">
            <Select
              allowClear
              options={Object.values(CollectionsSituation).map((situation) => ({
                value: situation,
                label: COLLECTIONS_SITUATION_LABELS[situation],
              }))}
            />
          </Form.Item>
        ) : null}
        {kind === "declaration" || kind === "settlement" ? (
          <Form.Item
            name="amountPln"
            label={kind === "declaration" ? "Kwota, którą zapłaci (zł)" : "Kwota jednej raty ugody (zł)"}
            rules={[{ required: true, message: "Podaj kwotę" }]}
          >
            <InputNumber className="w-full" min={1} precision={2} decimalSeparator="," />
          </Form.Item>
        ) : null}
        {kind === "declaration" ? (
          <Form.Item name="dueAt" label="Do kiedy" rules={[{ required: true, message: "Podaj termin" }]}>
            <DatePicker className="w-full" format="DD.MM.YYYY" disabledDate={(date) => date.isBefore(dayjs(), "day")} />
          </Form.Item>
        ) : null}
        {kind === "settlement" ? (
          <>
            <Form.Item
              name="installments"
              label="Liczba rat ugody"
              rules={[{ required: true, message: "Podaj liczbę rat" }]}
            >
              <InputNumber className="w-full" min={1} max={48} />
            </Form.Item>
            <Form.Item
              name="changeId"
              label="Id zmiany planu rat (opcjonalnie)"
              extra="Z okna zmiany planu rat w ustawieniach klientki."
            >
              <Input />
            </Form.Item>
          </>
        ) : null}
        {kind === "agency" ? (
          <>
            <Form.Item name="agency" label="Firma windykacyjna" rules={[{ required: true }]}>
              <Input />
            </Form.Item>
            <Form.Item name="reference" label="Numer sprawy u windykatora">
              <Input />
            </Form.Item>
          </>
        ) : null}
        <Form.Item
          name="note"
          label={kind === "note" ? "Treść" : "Notatka"}
          rules={kind === "note" ? [{ required: true, message: "Wpisz treść" }] : undefined}
        >
          <Input.TextArea rows={3} maxLength={2000} showCount />
        </Form.Item>
      </Form>
    </Modal>
  );
};

export default CaseActionModal;
